import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Button, Paper, Typography, IconButton, Tooltip, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Card, CardContent, Stack, Avatar, useTheme, alpha, Tabs, Tab, Badge,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Autocomplete, MenuItem,
  Menu, Fade, Divider
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon,
  Refresh as RefreshIcon, CheckCircle as PostIcon,
  Undo as UnpostIcon, ThumbUp as ApproveIcon, ThumbDown as RejectIcon,
  AttachMoney as MoneyIcon, ReceiptLong, MoreVert as MoreVertIcon,
  HourglassTop as PendingIcon, Block as BlockIcon, Visibility as ViewIcon,
  Print as PrintIcon, Close as CloseIcon, LocalShipping as ShippingIcon,
  TrendingUp as TrendingUpIcon, CheckCircle as CheckCircleIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';
import { useFiscalDateRange } from '../hooks/useFiscalDateRange';
import { useConfirm } from '../hooks/useConfirm';
import { internalIssuesService } from '../services/internalIssues.service';
import { InternalIssue } from '../types/internalIssues.types';
import { itemsService, itemUnitsService } from '../services/items.service';
import { warehousesService } from '../services/warehouses.service';
import { chartOfAccountsService } from '../services/chartOfAccounts.service';
import { companyInfoService, CompanyInfo } from '../services/companyInfo.service';
import config from '../config';

const InternalIssuesPage: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const { minDate, maxDate } = useFiscalDateRange();
  const confirm = useConfirm();

  const t = {
    title: isRTL ? 'سندات الصرف الداخلي' : 'Internal Issues',
    subtitle: isRTL ? 'استهلاك مباشر من المخزن لمصروف محدد' : 'Direct consumption from warehouse to a specific expense account',
    new: isRTL ? 'سند جديد' : 'New Issue',
    edit: isRTL ? 'تعديل' : 'Edit',
    view: isRTL ? 'عرض' : 'View',
    delete: isRTL ? 'حذف' : 'Delete',
    approve: isRTL ? 'اعتماد' : 'Approve',
    reject: isRTL ? 'رفض' : 'Reject',
    post: isRTL ? 'ترحيل' : 'Post Issue',
    unpost: isRTL ? 'إلغاء ترحيل' : 'Unpost',
    refresh: isRTL ? 'تحديث' : 'Refresh',
    search: isRTL ? 'بحث في السندات...' : 'Search issues...',
    all: isRTL ? 'الكل' : 'All',
    pending: isRTL ? 'بانتظار الاعتماد' : 'Pending',
    draft: isRTL ? 'معتمد (مسودة)' : 'Draft',
    posted: isRTL ? 'مرحّل' : 'Posted',
    rejected: isRTL ? 'مرفوض' : 'Rejected',
    issueNumber: isRTL ? 'رقم السند' : 'Issue #',
    issueDate: isRTL ? 'التاريخ' : 'Date',
    warehouse: isRTL ? 'المخزن' : 'Warehouse',
    consumedBy: isRTL ? 'المستلم' : 'Consumed By',
    purpose: isRTL ? 'الغرض' : 'Purpose',
    items: isRTL ? 'الأصناف' : 'Items',
    totalIssues: isRTL ? 'إجمالي السندات' : 'Total Issues',
    totalValue: isRTL ? 'إجمالي القيمة' : 'Total Value',
    confirmDelete: isRTL ? 'حذف هذا السند؟' : 'Delete this issue?',
    confirmPost: isRTL ? 'ترحيل السند؟ سيتم خصم المخزون.' : 'Post this issue? Stock will be deducted.',
    confirmUnpost: isRTL ? 'إلغاء ترحيل السند؟ سيتم استرجاع المخزون.' : 'Unpost this issue? Stock will be restored.',
    confirmApprove: isRTL ? 'اعتماد السند وإنشاء القيد المحاسبي؟' : 'Approve and create journal entry?',
    rejectDialogTitle: isRTL ? 'رفض السند' : 'Reject Internal Issue',
    rejectReason: isRTL ? 'سبب الرفض (اختياري)' : 'Rejection Reason (optional)',
    rejectConfirm: isRTL ? 'تأكيد الرفض' : 'Confirm Reject',
    journalEntry: isRTL ? 'ق.م.' : 'J.E.',
    fromDate: isRTL ? 'من تاريخ' : 'From Date',
    toDate: isRTL ? 'إلى تاريخ' : 'To Date',
    noData: isRTL ? 'لا توجد سندات' : 'No issues found',
  };

  // ─── State ───
  const [issues, setIssues] = useState<InternalIssue[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [itemFilter, setItemFilter] = useState<any | null>(null);

  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [expenseAccounts, setExpenseAccounts] = useState<any[]>([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIssue, setEditingIssue] = useState<InternalIssue | null>(null);
  const [previewIssue, setPreviewIssue] = useState<InternalIssue | null>(null);

  // Menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedIssueForMenu, setSelectedIssueForMenu] = useState<InternalIssue | null>(null);

  // Reject dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [issueToReject, setIssueToReject] = useState<InternalIssue | null>(null);

  // ─── Permissions from token ───
  const canPost = useMemo(() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;
      const payload = JSON.parse(atob(token.split('.')[1]));
      const perms = payload.Permission ? (Array.isArray(payload.Permission) ? payload.Permission : [payload.Permission]) : [];
      return perms.includes('INTERNAL_ISSUE_POST');
    } catch { return false; }
  }, []);

  // ─── Effects ───
  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [iss, whs, its, accs] = await Promise.all([
        internalIssuesService.getAll(),
        warehousesService.getAll(),
        itemsService.getAll(),
        chartOfAccountsService.getFinalAccounts(),
      ]);
      setIssues(iss);
      setWarehouses(whs.filter((w: any) => !w.branchId));
      setItems(its);
      setExpenseAccounts(accs.filter((a: any) => a.accountType === 'Expense' || a.accountType === 'CostOfGoodsSold'));
    } catch (e: any) {
      toast.error(e.response?.data?.message || (isRTL ? 'فشل التحميل' : 'Failed to load'));
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    try {
      const data = await internalIssuesService.getAll();
      setIssues(data);
    } catch { toast.error(isRTL ? 'فشل التحديث' : 'Refresh failed'); }
  };

  // ─── Filtered list ───
  const filtered = useMemo(() => {
    let list = issues;
    if (activeTab === 1) list = list.filter(i => i.status === 'Pending');
    else if (activeTab === 2) list = list.filter(i => i.status === 'Draft');
    else if (activeTab === 3) list = list.filter(i => i.status === 'Posted');
    else if (activeTab === 4) list = list.filter(i => i.status === 'Rejected');

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter(i =>
        i.issueNumber.toLowerCase().includes(term) ||
        (i.warehouseName || '').toLowerCase().includes(term) ||
        (i.consumedBy || '').toLowerCase().includes(term) ||
        (i.purpose || '').toLowerCase().includes(term)
      );
    }
    if (itemFilter) {
      list = list.filter(i => (i.details || []).some(d => d.itemId === itemFilter.itemId));
    }
    if (fromDate) {
      const fd = `${fromDate.getFullYear()}-${String(fromDate.getMonth() + 1).padStart(2, '0')}-${String(fromDate.getDate()).padStart(2, '0')}`;
      list = list.filter(i => i.issueDate.split('T')[0] >= fd);
    }
    if (toDate) {
      const td = `${toDate.getFullYear()}-${String(toDate.getMonth() + 1).padStart(2, '0')}-${String(toDate.getDate()).padStart(2, '0')}`;
      list = list.filter(i => i.issueDate.split('T')[0] <= td);
    }
    return [...list].sort((a, b) => b.issueNumber.localeCompare(a.issueNumber, undefined, { numeric: true }));
  }, [issues, activeTab, searchTerm, fromDate, toDate, itemFilter]);

  const stats = useMemo(() => ({
    total: issues.length,
    pending: issues.filter(i => i.status === 'Pending').length,
    draft: issues.filter(i => i.status === 'Draft').length,
    posted: issues.filter(i => i.status === 'Posted').length,
    rejected: issues.filter(i => i.status === 'Rejected').length,
    totalValue: issues.filter(i => i.status === 'Posted').reduce((s, i) => s + (i.totalAmount || 0), 0),
  }), [issues]);

  // ─── Actions ───
  const handleApprove = async (id: number) => {
    handleCloseMenu();
    if (!await confirm({ message: t.confirmApprove, severity: 'info' })) return;
    try {
      await internalIssuesService.approve(id);
      toast.success(isRTL ? 'تم الاعتماد' : 'Approved');
      refresh();
    } catch (e: any) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const handleOpenRejectDialog = (issue: InternalIssue) => {
    setIssueToReject(issue);
    setRejectReason('');
    setRejectDialogOpen(true);
    handleCloseMenu();
  };

  const handleConfirmReject = async () => {
    if (!issueToReject) return;
    try {
      await internalIssuesService.reject(issueToReject.issueId, rejectReason || undefined);
      toast.success(isRTL ? 'تم الرفض' : 'Rejected');
      setRejectDialogOpen(false);
      setIssueToReject(null);
      setRejectReason('');
      refresh();
    } catch (e: any) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const handlePost = async (id: number) => {
    handleCloseMenu();
    if (!await confirm({ message: t.confirmPost, severity: 'warning' })) return;
    try {
      await internalIssuesService.post(id);
      toast.success(isRTL ? 'تم الترحيل' : 'Posted');
      refresh();
    } catch (e: any) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const handleUnpost = async (id: number) => {
    handleCloseMenu();
    if (!await confirm({ message: t.confirmUnpost, severity: 'warning' })) return;
    try {
      await internalIssuesService.unpost(id);
      toast.success(isRTL ? 'تم إلغاء الترحيل' : 'Unposted');
      refresh();
    } catch (e: any) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const handleDelete = async (id: number) => {
    handleCloseMenu();
    if (!await confirm({ message: t.confirmDelete, severity: 'error' })) return;
    try {
      await internalIssuesService.delete(id);
      toast.success(isRTL ? 'تم الحذف' : 'Deleted');
      refresh();
    } catch (e: any) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const openCreate = () => { setEditingIssue(null); setDialogOpen(true); };
  const openEdit = async (issue: InternalIssue) => {
    handleCloseMenu();
    try {
      const full = await internalIssuesService.getById(issue.issueId);
      setEditingIssue(full);
      setDialogOpen(true);
    } catch { toast.error('Failed to load'); }
  };
  const openView = async (issue: InternalIssue) => {
    handleCloseMenu();
    try {
      const full = await internalIssuesService.getById(issue.issueId);
      setPreviewIssue(full);
    } catch { toast.error('Failed to load'); }
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, issue: InternalIssue) => {
    setAnchorEl(event.currentTarget);
    setSelectedIssueForMenu(issue);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedIssueForMenu(null);
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB', { year: 'numeric', month: 'short', day: 'numeric' });

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'Pending':
        return { color: 'warning' as const, icon: <PendingIcon />, borderColor: 'warning.main', label: t.pending };
      case 'Draft':
        return { color: 'info' as const, icon: <CheckCircleIcon />, borderColor: 'info.main', label: t.draft };
      case 'Posted':
        return { color: 'success' as const, icon: <CheckCircleIcon />, borderColor: 'success.main', label: t.posted };
      case 'Rejected':
        return { color: 'error' as const, icon: <BlockIcon />, borderColor: 'error.main', label: t.rejected };
      default:
        return { color: 'default' as const, icon: <PendingIcon />, borderColor: 'grey.400', label: status };
    }
  };

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ width: 56, height: 56, background: 'linear-gradient(135deg, #6A1B9A 0%, #AB47BC 100%)' }}>
            <ReceiptLong sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>{t.title}</Typography>
            <Typography variant="caption" color="text.secondary">
              {stats.total} {isRTL ? 'سند' : 'issues'}
              {stats.pending > 0 && (
                <Chip label={`${stats.pending} ${t.pending}`} size="small" color="warning" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
              )}
            </Typography>
          </Box>
        </Stack>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title={t.refresh}>
            <IconButton onClick={loadAll} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            size="large"
            sx={{
              background: 'linear-gradient(45deg, #6A1B9A 30%, #AB47BC 90%)',
              boxShadow: '0 3px 5px 2px rgba(106, 27, 154, .3)',
              px: 4,
              py: 1.5,
            }}
          >
            {t.new}
          </Button>
        </Box>
      </Stack>

      {/* Stat Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Card sx={{ background: 'linear-gradient(135deg, #6A1B9A 0%, #AB47BC 100%)', color: 'white' }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.totalIssues}</Typography>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>{stats.total}</Typography>
              </Box>
              <InventoryIcon sx={{ fontSize: 50, opacity: 0.3 }} />
            </Stack>
          </CardContent>
        </Card>
        <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.totalValue}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{stats.totalValue.toFixed(3)}</Typography>
                <Typography variant="caption">KD</Typography>
              </Box>
              <TrendingUpIcon sx={{ fontSize: 50, opacity: 0.3 }} />
            </Stack>
          </CardContent>
        </Card>
        <Card sx={{ background: 'linear-gradient(135deg, #ff9a56 0%, #ff6a00 100%)', color: 'white' }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.pending}</Typography>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>{stats.pending}</Typography>
              </Box>
              <PendingIcon sx={{ fontSize: 50, opacity: 0.3 }} />
            </Stack>
          </CardContent>
        </Card>
        <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
          <CardContent>
            <Stack direction="row" spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{stats.draft}</Typography>
                <Typography variant="caption">{t.draft}</Typography>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{stats.posted}</Typography>
                <Typography variant="caption">{t.posted}</Typography>
              </Box>
              {stats.rejected > 0 && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#fee2e2' }}>{stats.rejected}</Typography>
                    <Typography variant="caption">{t.rejected}</Typography>
                  </Box>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* Filter + Tabs */}
      <Paper elevation={3} sx={{
        mb: 3,
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, #1e3a5f 0%, #2c5282 100%)'
          : 'linear-gradient(135deg, #e0e7ff 0%, #f3e8ff 100%)',
        borderRadius: 3
      }}>
        <Stack spacing={2} sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              fullWidth
              placeholder={t.search}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ backgroundColor: 'background.paper', borderRadius: 2, flex: 1.6 }}
              InputProps={{
                startAdornment: <SearchIcon color="primary" sx={{ mr: 1 }} />,
              }}
            />
            <Autocomplete
              size="medium"
              sx={{ backgroundColor: 'background.paper', borderRadius: 2, minWidth: 240, flex: 1 }}
              options={items}
              value={itemFilter}
              onChange={(_, v) => setItemFilter(v)}
              getOptionLabel={(o: any) => `${o.itemCode} — ${isRTL ? (o.itemNameAr || o.itemName) : o.itemName}`}
              isOptionEqualToValue={(o: any, v: any) => o.itemId === v.itemId}
              filterOptions={(opts, state) => {
                const q = state.inputValue.toLowerCase().trim();
                if (!q) return opts.slice(0, 100);
                return opts.filter((o: any) =>
                  o.itemCode.toLowerCase().includes(q) ||
                  (o.itemName || '').toLowerCase().includes(q) ||
                  (o.itemNameAr || '').includes(state.inputValue)
                ).slice(0, 200);
              }}
              renderInput={(params) => (
                <TextField {...params} label={isRTL ? 'تصفية بالصنف' : 'Filter by Item'} placeholder={isRTL ? 'ابحث عن صنف...' : 'Search item...'} />
              )}
              clearOnEscape
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t.fromDate}
                value={fromDate}
                onChange={setFromDate}
                format="dd/MM/yyyy"
                minDate={minDate}
                maxDate={toDate || maxDate}
                slotProps={{ textField: { sx: { backgroundColor: 'background.paper', borderRadius: 2, minWidth: 170 } } }}
              />
              <DatePicker
                label={t.toDate}
                value={toDate}
                onChange={setToDate}
                format="dd/MM/yyyy"
                minDate={fromDate || minDate}
                maxDate={maxDate}
                slotProps={{ textField: { sx: { backgroundColor: 'background.paper', borderRadius: 2, minWidth: 170 } } }}
              />
            </LocalizationProvider>
          </Stack>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab label={<Badge badgeContent={stats.total} color="primary"><span>{t.all}</span></Badge>} />
            <Tab label={<Badge badgeContent={stats.pending} color="warning"><span>{t.pending}</span></Badge>} />
            <Tab label={<Badge badgeContent={stats.draft} color="info"><span>{t.draft}</span></Badge>} />
            <Tab label={<Badge badgeContent={stats.posted} color="success"><span>{t.posted}</span></Badge>} />
            {stats.rejected > 0 && (
              <Tab label={<Badge badgeContent={stats.rejected} color="error"><span>{t.rejected}</span></Badge>} />
            )}
          </Tabs>
        </Stack>
      </Paper>

      {/* Cards list */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <Stack spacing={2}>
          {filtered.map((issue, idx) => {
            const cfg = getStatusConfig(issue.status || 'Pending');
            return (
              <Fade in timeout={300 + idx * 60} key={issue.issueId}>
                <Card elevation={3} sx={{
                  transition: 'all 0.3s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 8 },
                  border: 2,
                  borderColor: cfg.borderColor,
                  borderLeft: 6,
                  borderLeftColor: cfg.borderColor,
                  ...(issue.status === 'Pending' && {
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.warning.main, 0.4)}` },
                      '70%': { boxShadow: `0 0 0 10px ${alpha(theme.palette.warning.main, 0)}` },
                      '100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.warning.main, 0)}` },
                    },
                  }),
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ width: 50, height: 50, bgcolor: `${cfg.color}.main` }}>
                          <ReceiptLong />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>{issue.issueNumber}</Typography>
                          <Typography variant="body2" color="text.secondary">{formatDate(issue.issueDate)}</Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={cfg.label} color={cfg.color} icon={cfg.icon} sx={{ fontWeight: 600, minWidth: 100 }} />
                        {issue.status === 'Draft' && issue.journalEntryId && (
                          <Chip
                            label={`${t.journalEntry} ${issue.journalEntryNumber || ''} (#${issue.journalEntryId})`}
                            size="small" color="info" variant="outlined"
                          />
                        )}
                        {issue.status === 'Posted' && issue.journalEntryNumber && (
                          <Chip
                            label={`${t.journalEntry} ${issue.journalEntryNumber}`}
                            size="small" color="success" variant="outlined"
                          />
                        )}
                        {issue.status === 'Pending' && (
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title={t.approve}>
                              <IconButton size="small" onClick={() => handleApprove(issue.issueId)}
                                sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.25) } }}>
                                <ApproveIcon fontSize="small" color="success" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t.reject}>
                              <IconButton size="small" onClick={() => handleOpenRejectDialog(issue)}
                                sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.25) } }}>
                                <RejectIcon fontSize="small" color="error" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        )}
                        <IconButton onClick={(e) => handleOpenMenu(e, issue)}><MoreVertIcon /></IconButton>
                      </Stack>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 3 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t.warehouse}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {isRTL ? (issue.warehouseNameAr || issue.warehouseName) : issue.warehouseName}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t.consumedBy}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{issue.consumedBy || '-'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t.purpose}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{issue.purpose || '-'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t.items} / {t.totalValue}</Typography>
                        <Stack direction="row" spacing={1.5} alignItems="baseline">
                          <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>{issue.details?.length || 0}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                            {(issue.totalAmount || 0).toFixed(3)} KD
                          </Typography>
                        </Stack>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Fade>
            );
          })}
          {filtered.length === 0 && (
            <Paper sx={{ p: 8, textAlign: 'center', bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
              <ReceiptLong sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">{t.noData}</Typography>
            </Paper>
          )}
        </Stack>
      )}

      {/* Action Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu} PaperProps={{ elevation: 3, sx: { minWidth: 220, mt: 1 } }}>
        <MenuItem onClick={() => selectedIssueForMenu && openView(selectedIssueForMenu)}>
          <ViewIcon fontSize="small" sx={{ mr: 1 }} color="primary" />
          <Typography color="primary.main">{t.view}</Typography>
        </MenuItem>
        <Divider />
        {selectedIssueForMenu?.status === 'Pending' && (
          <MenuItem onClick={() => handleApprove(selectedIssueForMenu.issueId)}>
            <ApproveIcon fontSize="small" sx={{ mr: 1 }} color="success" />
            <Typography color="success.main" fontWeight={600}>{t.approve}</Typography>
          </MenuItem>
        )}
        {selectedIssueForMenu?.status === 'Pending' && (
          <MenuItem onClick={() => handleOpenRejectDialog(selectedIssueForMenu)}>
            <RejectIcon fontSize="small" sx={{ mr: 1 }} color="error" />
            <Typography color="error.main" fontWeight={600}>{t.reject}</Typography>
          </MenuItem>
        )}
        {(selectedIssueForMenu?.status === 'Pending' || selectedIssueForMenu?.status === 'Draft') && (
          <MenuItem onClick={() => selectedIssueForMenu && openEdit(selectedIssueForMenu)}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />{t.edit}
          </MenuItem>
        )}
        {selectedIssueForMenu?.status === 'Draft' && canPost && (
          <MenuItem onClick={() => handlePost(selectedIssueForMenu.issueId)}>
            <PostIcon fontSize="small" sx={{ mr: 1 }} color="success" />
            <Typography color="success.main">{t.post}</Typography>
          </MenuItem>
        )}
        {selectedIssueForMenu?.status === 'Posted' && canPost && (
          <MenuItem onClick={() => handleUnpost(selectedIssueForMenu.issueId)}>
            <UnpostIcon fontSize="small" sx={{ mr: 1 }} color="warning" />
            <Typography color="warning.main">{t.unpost}</Typography>
          </MenuItem>
        )}
        {(selectedIssueForMenu?.status === 'Pending' || selectedIssueForMenu?.status === 'Draft' || selectedIssueForMenu?.status === 'Rejected') && (
          <MenuItem onClick={() => selectedIssueForMenu && handleDelete(selectedIssueForMenu.issueId)}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} color="error" />
            <Typography color="error">{t.delete}</Typography>
          </MenuItem>
        )}
      </Menu>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <RejectIcon />
          {t.rejectDialogTitle}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 2 }}>
          {issueToReject && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Chip label={issueToReject.issueNumber} color="primary" />
                <Typography variant="body2" color="text.secondary">
                  {isRTL ? (issueToReject.warehouseNameAr || issueToReject.warehouseName) : issueToReject.warehouseName}
                </Typography>
              </Stack>
              <TextField
                fullWidth multiline rows={3}
                label={t.rejectReason}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder={isRTL ? 'مثال: الصنف غير مناسب للاستهلاك' : 'e.g. Item not suitable for consumption'}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRejectDialogOpen(false)} variant="outlined">{isRTL ? 'إلغاء' : 'Cancel'}</Button>
          <Button onClick={handleConfirmReject} variant="contained" color="error" startIcon={<RejectIcon />}>{t.rejectConfirm}</Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Dialog */}
      {dialogOpen && (
        <InternalIssueDialog
          open={dialogOpen}
          editing={editingIssue}
          warehouses={warehouses}
          items={items}
          expenseAccounts={expenseAccounts}
          isRTL={isRTL}
          minDate={minDate}
          maxDate={maxDate}
          onClose={() => { setDialogOpen(false); setEditingIssue(null); }}
          onSaved={() => { setDialogOpen(false); setEditingIssue(null); refresh(); }}
        />
      )}

      {/* Preview Dialog with Print */}
      {previewIssue && (
        <InternalIssuePreviewDialog
          open={!!previewIssue}
          onClose={() => setPreviewIssue(null)}
          issue={previewIssue}
          isRTL={isRTL}
          onEdit={() => { const i = previewIssue; setPreviewIssue(null); if (i) openEdit(i); }}
        />
      )}
    </Box>
  );
};

// ════════════════════ Preview Dialog with Print ═══════════════════════
const InternalIssuePreviewDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  issue: InternalIssue;
  isRTL: boolean;
  onEdit: () => void;
}> = ({ open, onClose, issue, isRTL, onEdit }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  useEffect(() => {
    if (open) {
      companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {});
    }
  }, [open]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'Pending': return isRTL ? 'بانتظار الاعتماد' : 'Pending';
      case 'Draft': return isRTL ? 'معتمد (مسودة)' : 'Draft';
      case 'Posted': return isRTL ? 'مرحّل' : 'Posted';
      case 'Rejected': return isRTL ? 'مرفوض' : 'Rejected';
      default: return status || '-';
    }
  };

  const handlePrint = () => {
    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) return;
    const themeColor = '#6A1B9A';
    const totalValue = issue.details?.reduce((s, d) => s + (d.totalCost || d.quantity * d.unitCost), 0) || 0;
    w.document.write(`<!DOCTYPE html><html dir="${isRTL ? 'rtl' : 'ltr'}"><head><meta charset="UTF-8">
<title>${issue.issueNumber}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Segoe UI',Arial,sans-serif; padding:25px; font-size:12px; direction:${isRTL ? 'rtl' : 'ltr'}; }
  .header { text-align:center; border-bottom:3px solid ${themeColor}; padding-bottom:12px; margin-bottom:15px; }
  .header img { max-height:55px; margin-bottom:5px; }
  .header h2 { color:${themeColor}; margin:3px 0; }
  .doc-title { text-align:center; margin-bottom:15px; }
  .doc-title h3 { color:${themeColor}; border:2px solid ${themeColor}; display:inline-block; padding:6px 25px; border-radius:6px; }
  .info { display:grid; grid-template-columns:1fr 1fr; gap:8px; border:1px solid #ddd; border-radius:6px; padding:12px; margin-bottom:15px; }
  .info-item label { font-size:10px; color:#666; display:block; } .info-item span { font-weight:bold; }
  .section { background:${themeColor}; color:white; padding:6px 12px; font-weight:bold; font-size:11px; }
  table { width:100%; border-collapse:collapse; } th { background:#f5f5f5; padding:6px; font-size:11px; border:1px solid #ddd; text-align:${isRTL?'right':'left'}; }
  td { padding:5px 6px; border:1px solid #eee; font-size:11px; } .amount { text-align:${isRTL?'left':'right'}; font-weight:bold; }
  .total-row td { background:${themeColor}; color:white; font-weight:bold; font-size:12px; }
  .signatures { display:flex; justify-content:space-between; margin-top:50px; }
  .sig-box { text-align:center; width:30%; } .sig-line { border-top:1px solid #333; margin-top:50px; padding-top:6px; font-weight:bold; }
  @media print { @page { size:A4; margin:1cm; } }
</style></head><body>
${companyInfo ? `<div class="header">${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" onerror="this.style.display='none'" />` : ''}<h2>${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2><div style="font-size:10px;color:#666">${companyInfo.address||''} ${companyInfo.phone ? '| '+companyInfo.phone : ''} ${companyInfo.email ? '| '+companyInfo.email : ''}</div></div>` : ''}
<div class="doc-title"><h3>${isRTL ? 'سند صرف داخلي' : 'Internal Issue'}</h3></div>
<div class="info">
  <div class="info-item"><label>${isRTL?'رقم السند':'Issue No.'}</label><span>${issue.issueNumber}</span></div>
  <div class="info-item"><label>${isRTL?'التاريخ':'Date'}</label><span>${formatDate(issue.issueDate)}</span></div>
  <div class="info-item"><label>${isRTL?'المخزن':'Warehouse'}</label><span>${isRTL ? (issue.warehouseNameAr || issue.warehouseName) : issue.warehouseName}</span></div>
  <div class="info-item"><label>${isRTL?'الحالة':'Status'}</label><span>${getStatusLabel(issue.status)}</span></div>
  ${issue.consumedBy ? `<div class="info-item"><label>${isRTL?'المستلم':'Consumed By'}</label><span>${issue.consumedBy}</span></div>` : ''}
  ${issue.purpose ? `<div class="info-item"><label>${isRTL?'الغرض':'Purpose'}</label><span>${issue.purpose}</span></div>` : ''}
  ${issue.journalEntryNumber ? `<div class="info-item"><label>${isRTL?'القيد المحاسبي':'Journal Entry'}</label><span>${issue.journalEntryNumber}</span></div>` : ''}
  ${issue.notes ? `<div class="info-item" style="grid-column:span 2"><label>${isRTL?'ملاحظات':'Notes'}</label><span>${issue.notes}</span></div>` : ''}
</div>
<div class="section">${isRTL ? '📦 تفاصيل الأصناف وحسابات المصروف' : '📦 Items & Expense Accounts'}</div>
<table><thead><tr><th>#</th><th>${isRTL?'الصنف':'Item'}</th><th>${isRTL?'الوحدة':'Unit'}</th><th class="amount">${isRTL?'الكمية':'Qty'}</th><th class="amount">${isRTL?'التكلفة':'Cost'}</th><th class="amount">${isRTL?'الإجمالي':'Total'}</th><th>${isRTL?'حساب المصروف':'Expense Account'}</th></tr></thead><tbody>
${(issue.details||[]).map((d: any, i: number) => `<tr><td>${i+1}</td><td>${d.itemCode || ''} — ${isRTL ? (d.itemNameAr||d.itemName) : d.itemName}</td><td>${isRTL ? (d.unitNameAr || d.unitName) : d.unitName}</td><td class="amount">${d.quantity.toFixed(3)}</td><td class="amount">${(d.unitCost||0).toFixed(3)}</td><td class="amount">${(d.totalCost || d.quantity * d.unitCost).toFixed(3)}</td><td>${d.expenseAccountCode || ''}<br/><span style="font-size:10px;color:#666">${isRTL ? (d.expenseAccountNameAr || d.expenseAccountName) : d.expenseAccountName}</span></td></tr>`).join('')}
<tr class="total-row"><td colspan="5" style="text-align:${isRTL?'left':'right'}">${isRTL?'الإجمالي':'Grand Total'}</td><td class="amount">${totalValue.toFixed(3)} KD</td><td></td></tr>
</tbody></table>
<div class="signatures">
  <div class="sig-box"><div class="sig-line">${isRTL?'أمين المخزن':'Warehouse Keeper'}</div></div>
  <div class="sig-box"><div class="sig-line">${isRTL?'المستلم':'Recipient'}</div></div>
  <div class="sig-box"><div class="sig-line">${isRTL?'المحاسب':'Accountant'}</div></div>
</div></body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 500);
  };

  const totalValue = issue.details?.reduce((s, d) => s + (d.totalCost || 0), 0) || 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth dir={isRTL ? 'rtl' : 'ltr'} PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{
        background: isDark
          ? 'linear-gradient(135deg, #4a1a6e 0%, #2d0f4d 100%)'
          : 'linear-gradient(135deg, #6A1B9A 0%, #AB47BC 100%)',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptLong />
          <Box component="span">{isRTL ? 'معاينة سند صرف داخلي' : 'Internal Issue Preview'}</Box>
        </Box>
        <Box>
          <Tooltip title={isRTL ? 'طباعة' : 'Print'}>
            <IconButton onClick={handlePrint} sx={{ color: 'white' }}><PrintIcon /></IconButton>
          </Tooltip>
          {(issue.status === 'Pending' || issue.status === 'Draft') && (
            <Tooltip title={isRTL ? 'تعديل' : 'Edit'}>
              <IconButton onClick={onEdit} sx={{ color: 'white' }}><EditIcon /></IconButton>
            </Tooltip>
          )}
          <IconButton onClick={onClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ mt: 3 }}>
        {/* ─── Header Info Section ─── */}
        <Paper elevation={2} sx={{
          p: 3, mb: 3,
          bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5',
          borderRadius: 2
        }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'رقم السند' : 'Issue Number'}
              </Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#6A1B9A' }}>
                {issue.issueNumber}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'التاريخ' : 'Date'}
              </Typography>
              <Typography variant="body1" fontWeight="bold">{formatDate(issue.issueDate)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'الحالة' : 'Status'}
              </Typography>
              <Box><Chip label={getStatusLabel(issue.status)} size="small" sx={{ fontWeight: 'bold' }} /></Box>
            </Box>
            {issue.journalEntryNumber && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  {isRTL ? 'القيد المحاسبي' : 'Journal Entry'}
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="success.main">
                  {issue.journalEntryNumber} ({issue.journalEntryStatus})
                </Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Warehouse Section Card */}
          <Box sx={{
            p: 2,
            bgcolor: isDark ? 'rgba(106,27,154,0.15)' : '#f3e5f5',
            borderRadius: 1,
            borderLeft: 4,
            borderColor: '#6A1B9A'
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
              {isRTL ? 'المخزن المصدر' : 'Source Warehouse'}
            </Typography>
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#6A1B9A' }}>
              {isRTL ? (issue.warehouseNameAr || issue.warehouseName) : issue.warehouseName}
            </Typography>
            {(issue as any).warehouseCode && (
              <Typography variant="caption" color="text.secondary">{(issue as any).warehouseCode}</Typography>
            )}
          </Box>

          {/* Consumed By + Purpose */}
          {(issue.consumedBy || issue.purpose) && (
            <Box sx={{
              mt: 2, p: 2,
              bgcolor: isDark ? 'rgba(76,175,80,0.1)' : '#e8f5e9',
              borderRadius: 1,
              borderLeft: 4,
              borderColor: 'success.main'
            }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                {issue.consumedBy && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                      {isRTL ? 'المستلم' : 'Consumed By'}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="success.main">{issue.consumedBy}</Typography>
                  </Box>
                )}
                {issue.purpose && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                      {isRTL ? 'الغرض' : 'Purpose'}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="success.main">{issue.purpose}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {issue.notes && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                {isRTL ? 'ملاحظات:' : 'Notes:'}
              </Typography>
              <Typography variant="body2">{issue.notes}</Typography>
            </Box>
          )}
        </Paper>

        {/* ─── Items Details Section ─── */}
        <Paper elevation={2} sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{
            bgcolor: isDark ? 'rgba(106,27,154,0.4)' : '#6A1B9A',
            color: 'white',
            p: 1.5,
            fontWeight: 'bold'
          }}>
            {isRTL ? '📦 تفاصيل الأصناف وحسابات المصروف' : '📦 Items & Expense Accounts'}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الصنف' : 'Item'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الوحدة' : 'Unit'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الكمية' : 'Qty'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'التكلفة' : 'Cost'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الإجمالي' : 'Total'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'حساب المصروف' : 'Expense Account'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {issue.details.map((d, idx) => (
                  <TableRow key={d.detailId} hover>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{idx + 1}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      <strong>{d.itemCode}</strong>
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {isRTL ? (d.itemNameAr || d.itemName) : d.itemName}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{isRTL ? (d.unitNameAr || d.unitName) : d.unitName}</TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>{d.quantity.toFixed(3)}</TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>{d.unitCost.toFixed(3)}</TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#6A1B9A' }}>{d.totalCost.toFixed(3)}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      <strong style={{ fontFamily: 'monospace' }}>{d.expenseAccountCode}</strong>
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {isRTL ? (d.expenseAccountNameAr || d.expenseAccountName) : d.expenseAccountName}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}

                {/* ─── Grand Total Row ─── */}
                <TableRow>
                  <TableCell colSpan={5} align="right" sx={{
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    bgcolor: isDark ? 'rgba(106,27,154,0.25)' : 'rgba(106,27,154,0.12)',
                    color: isDark ? '#fff' : '#6A1B9A',
                    borderTop: '2px solid #6A1B9A'
                  }}>
                    {isRTL ? 'الإجمالي الكلي:' : 'Grand Total:'}
                  </TableCell>
                  <TableCell align="center" sx={{
                    fontWeight: 'bold',
                    fontSize: '1.15rem',
                    color: 'success.main',
                    bgcolor: isDark ? 'rgba(106,27,154,0.25)' : 'rgba(106,27,154,0.12)',
                    borderTop: '2px solid #6A1B9A'
                  }}>
                    {totalValue.toFixed(3)} {isRTL ? 'د.ك' : 'KD'}
                  </TableCell>
                  <TableCell sx={{
                    bgcolor: isDark ? 'rgba(106,27,154,0.25)' : 'rgba(106,27,154,0.12)',
                    borderTop: '2px solid #6A1B9A'
                  }} />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>{isRTL ? 'إغلاق' : 'Close'}</Button>
        <Button onClick={handlePrint} variant="contained" startIcon={<PrintIcon />}
          sx={{ background: 'linear-gradient(135deg, #6A1B9A 0%, #AB47BC 100%)' }}>
          {isRTL ? 'طباعة' : 'Print'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ════════════════════ Create/Edit Dialog ═══════════════════════════
interface DialogLine {
  key: number;
  detailId?: number;
  itemId: number | null;
  unitId: number | null;
  quantity: number;
  expenseAccountId: number | null;
  notes: string;
  availableUnits: any[];
}

const InternalIssueDialog: React.FC<{
  open: boolean;
  editing: InternalIssue | null;
  warehouses: any[];
  items: any[];
  expenseAccounts: any[];
  isRTL: boolean;
  minDate?: Date;
  maxDate?: Date;
  onClose: () => void;
  onSaved: () => void;
}> = ({ open, editing, warehouses, items, expenseAccounts, isRTL, minDate, maxDate, onClose, onSaved }) => {
  const [issueNumber, setIssueNumber] = useState('');
  const [issueDate, setIssueDate] = useState<Date>(new Date());
  const [warehouseId, setWarehouseId] = useState<number | null>(null);
  const [consumedBy, setConsumedBy] = useState('');
  const [purpose, setPurpose] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DialogLine[]>([]);
  const [keyCounter, setKeyCounter] = useState(1);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setIssueNumber(editing.issueNumber);
      setIssueDate(new Date(editing.issueDate));
      setWarehouseId(editing.warehouseId);
      setConsumedBy(editing.consumedBy || '');
      setPurpose(editing.purpose || '');
      setNotes(editing.notes || '');
      let k = 1;
      Promise.all(editing.details.map(async (d) => {
        let units: any[] = [];
        try { units = await itemUnitsService.getItemUnits(d.itemId); } catch {}
        return {
          key: k++,
          detailId: d.detailId,
          itemId: d.itemId,
          unitId: d.unitId,
          quantity: d.quantity,
          expenseAccountId: d.expenseAccountId,
          notes: d.notes || '',
          availableUnits: units,
        };
      })).then((arr: any) => {
        setLines(arr);
        setKeyCounter(k);
      });
    } else {
      generateNumber();
      setIssueDate(new Date());
      setWarehouseId(null);
      setConsumedBy(''); setPurpose(''); setNotes('');
      setLines([{ key: 1, itemId: null, unitId: null, quantity: 0, expenseAccountId: null, notes: '', availableUnits: [] }]);
      setKeyCounter(2);
    }
  }, [editing]);

  const generateNumber = () => {
    const d = new Date();
    const num = `II-${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}-${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}${String(d.getSeconds()).padStart(2, '0')}`;
    setIssueNumber(num);
  };

  const onItemChange = async (key: number, itemId: number | null) => {
    let units: any[] = [];
    if (itemId) {
      try { units = await itemUnitsService.getItemUnits(itemId); } catch {}
    }
    setLines(prev => prev.map(l => l.key === key ? { ...l, itemId, unitId: units.length > 0 ? units[0].unitId : null, availableUnits: units } : l));
  };

  const updateLine = (key: number, field: keyof DialogLine, value: any) => {
    setLines(prev => prev.map(l => l.key === key ? { ...l, [field]: value } : l));
  };

  const addLine = () => {
    setLines(prev => [...prev, { key: keyCounter, itemId: null, unitId: null, quantity: 0, expenseAccountId: null, notes: '', availableUnits: [] }]);
    setKeyCounter(c => c + 1);
  };

  const removeLine = (key: number) => {
    setLines(prev => prev.filter(l => l.key !== key));
  };

  const handleSave = async () => {
    if (!warehouseId) { toast.error(isRTL ? 'اختر المخزن' : 'Select warehouse'); return; }
    const valid = lines.filter(l => l.itemId && l.unitId && l.quantity > 0 && l.expenseAccountId);
    if (valid.length === 0) { toast.error(isRTL ? 'أضف سطراً صحيحاً' : 'Add at least one valid line'); return; }

    const dateStr = `${issueDate.getFullYear()}-${String(issueDate.getMonth() + 1).padStart(2, '0')}-${String(issueDate.getDate()).padStart(2, '0')}`;

    setSaving(true);
    try {
      if (editing) {
        await internalIssuesService.update(editing.issueId, {
          issueId: editing.issueId,
          issueDate: dateStr,
          warehouseId,
          consumedBy: consumedBy || undefined,
          purpose: purpose || undefined,
          notes: notes || undefined,
          details: valid.map(l => ({
            detailId: l.detailId || 0,
            itemId: l.itemId!,
            unitId: l.unitId!,
            quantity: l.quantity,
            expenseAccountId: l.expenseAccountId!,
            notes: l.notes || undefined,
          })),
        });
        toast.success(isRTL ? 'تم التحديث' : 'Updated');
      } else {
        await internalIssuesService.create({
          issueNumber,
          issueDate: dateStr,
          warehouseId,
          consumedBy: consumedBy || undefined,
          purpose: purpose || undefined,
          notes: notes || undefined,
          details: valid.map(l => ({
            itemId: l.itemId!,
            unitId: l.unitId!,
            quantity: l.quantity,
            expenseAccountId: l.expenseAccountId!,
            notes: l.notes || undefined,
          })),
        });
        toast.success(isRTL ? 'تم الإنشاء' : 'Created');
      }
      onSaved();
    } catch (e: any) {
      toast.error(e.response?.data?.message || (isRTL ? 'فشل الحفظ' : 'Save failed'));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth dir={isRTL ? 'rtl' : 'ltr'} PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #6A1B9A 0%, #AB47BC 100%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 2
      }}>
        <ReceiptLong />
        {editing ? (isRTL ? 'تعديل سند صرف داخلي' : 'Edit Internal Issue') : (isRTL ? 'سند صرف داخلي جديد' : 'New Internal Issue')}
      </DialogTitle>
      <DialogContent sx={{ pt: '20px !important' }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField label={isRTL ? 'رقم السند' : 'Issue Number'} value={issueNumber}
              onChange={e => setIssueNumber(e.target.value)} fullWidth size="small"
              disabled={!!editing} />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker label={isRTL ? 'التاريخ' : 'Date'} value={issueDate}
                onChange={(d) => d && setIssueDate(d)} format="dd/MM/yyyy"
                minDate={minDate} maxDate={maxDate}
                slotProps={{ textField: { size: 'small', fullWidth: true } }} />
            </LocalizationProvider>
            <Autocomplete size="small" fullWidth
              options={warehouses}
              getOptionLabel={(o: any) => `${o.warehouseCode} - ${isRTL ? (o.warehouseNameAr || o.warehouseName) : o.warehouseName}`}
              value={warehouses.find(w => w.warehouseId === warehouseId) || null}
              onChange={(_, v) => setWarehouseId(v?.warehouseId || null)}
              renderInput={p => <TextField {...p} label={isRTL ? 'المخزن' : 'Warehouse'} required />} />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField label={isRTL ? 'المستلم' : 'Consumed By'} value={consumedBy}
              onChange={e => setConsumedBy(e.target.value)} fullWidth size="small" />
            <TextField label={isRTL ? 'الغرض' : 'Purpose'} value={purpose}
              onChange={e => setPurpose(e.target.value)} fullWidth size="small" />
          </Stack>
          <TextField label={isRTL ? 'ملاحظات' : 'Notes'} value={notes}
            onChange={e => setNotes(e.target.value)} fullWidth size="small" multiline rows={2} />

          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>{isRTL ? 'بنود السند' : 'Items'}</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addLine}>{isRTL ? 'إضافة سطر' : 'Add Line'}</Button>
            </Stack>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ width: 40 }}>#</TableCell>
                    <TableCell sx={{ minWidth: 280 }}>{isRTL ? 'الصنف' : 'Item'}</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>{isRTL ? 'الوحدة' : 'Unit'}</TableCell>
                    <TableCell sx={{ minWidth: 110 }}>{isRTL ? 'الكمية' : 'Qty'}</TableCell>
                    <TableCell sx={{ minWidth: 320 }}>{isRTL ? 'حساب المصروف' : 'Expense Account'}</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>{isRTL ? 'ملاحظات' : 'Notes'}</TableCell>
                    <TableCell sx={{ width: 50 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((l, idx) => (
                    <TableRow key={l.key}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        <Autocomplete size="small" fullWidth
                          options={items}
                          getOptionLabel={(o: any) => `${o.itemCode} - ${isRTL ? (o.itemNameAr || o.itemName) : o.itemName}`}
                          value={items.find((i: any) => i.itemId === l.itemId) || null}
                          onChange={(_, v: any) => onItemChange(l.key, v?.itemId || null)}
                          renderInput={p => <TextField {...p} placeholder={isRTL ? 'اختر' : 'Select'} />} />
                      </TableCell>
                      <TableCell>
                        <TextField select size="small" fullWidth
                          value={l.unitId || ''}
                          onChange={e => updateLine(l.key, 'unitId', e.target.value ? Number(e.target.value) : null)}>
                          <MenuItem value="" disabled>{isRTL ? 'اختر' : 'Select'}</MenuItem>
                          {l.availableUnits.map((u: any) => (
                            <MenuItem key={u.unitId} value={u.unitId}>{isRTL ? (u.unitNameAr || u.unitName) : u.unitName}</MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField size="small" fullWidth
                          value={l.quantity || ''}
                          onChange={e => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) updateLine(l.key, 'quantity', v === '' ? 0 : parseFloat(v)); }}
                          inputProps={{ inputMode: 'decimal' }} />
                      </TableCell>
                      <TableCell>
                        <Autocomplete size="small" fullWidth
                          options={expenseAccounts}
                          getOptionLabel={(o: any) => `${o.accountCode} - ${isRTL ? (o.accountNameAr || o.accountName) : o.accountName}`}
                          value={expenseAccounts.find((a: any) => a.accountId === l.expenseAccountId) || null}
                          onChange={(_, v: any) => updateLine(l.key, 'expenseAccountId', v?.accountId || null)}
                          renderInput={p => <TextField {...p} placeholder={isRTL ? 'حساب المصروف' : 'Expense Account'} />} />
                      </TableCell>
                      <TableCell>
                        <TextField size="small" fullWidth value={l.notes}
                          onChange={e => updateLine(l.key, 'notes', e.target.value)} />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" color="error" onClick={() => removeLine(l.key)}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>{isRTL ? 'إلغاء' : 'Cancel'}</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          sx={{ background: 'linear-gradient(135deg, #6A1B9A 0%, #AB47BC 100%)' }}>
          {saving ? (isRTL ? 'جاري...' : 'Saving...') : (isRTL ? 'حفظ' : 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InternalIssuesPage;
